//single position

// React
import React, { useCallback, useContext, useEffect, useState } from "react";

// Components
import { Grid, Row, Col } from "../../../components/Grid";
import { BoxLoader, Icon, TogglePosition, Modal } from "../../../components";
import { MegaHeader } from "../../components";
import { Typography } from "../../../components/Typography";
import { getGenericLocalStorage, setGenericLocalStorage } from "../../../utils/localSave";
import EditPositionView from "./EditPositionView/EditPositionView";
import { ApplicationContextWrapper } from "../../components/ApplicationList";
import Kanban from "./Kanban/Kanban";
import InviteCandidate from "../Modals/InviteCandidate/InviteCandidate";
import MoveSelectedApplications from "../Modals/MoveSelectedApplications/MoveSelectedApplications";
import PromoteHiBoost from "../Modals/PromoteHiBoost/PromoteHiBoost";
// Routing and views
import { useHistory, useParams } from "react-router-dom";
import AllPositions from "../AllPositions/AllPositions";
import Application from "../Application/Application";

// API Endpoints - Custom hooks - utility functions
import { getPositions } from "../../../api/endpoints/position";
import { formatDate } from "../../../utils/various";

// State management
import { DispatchPositionContext, StatePositionContext } from "../../context/PositionContext";
import { DispatchErrorContext } from "../../../context/ErrorContext";
import { useKanbanState } from "../../context/KanbanContext";

// Translations
import { useTranslation } from "react-i18next";

// Styled components and React query
import { SinglePositionContainer } from "./Position.styled";
import { StyledTopBar, TopBarBottom, TopBarTop, TopBarBottomContent } from "../../components/TopBar/style";
import { EditButton, PrimaryButton } from "../../../components/Button";
import { Flex } from "../../style";
import { MobileOnly, DesktopOnly, HiddenBetween } from "../../style";
// import { use } from "react";
import PositionPublish from "../../components/cp/SinglePage/PageMetaEditor/MetaGroups/PublishMG/PositionPublish";
import { PageContext } from "../../context/PageContext";
import { getPositionPage } from "../../../api/endpoints/cp";
import AnonymousHiring from "./AnonymousHiring/AnonymousHiring";
import ManageShareholders from "../../components/Shortlist/ManageShareholders/ManageShareholders";

export const Position = () => {
  const { t } = useTranslation();
  const { single, isLoading, count, collapsed, editPosition } = useContext(StatePositionContext);
  const positionDispatch = useContext(DispatchPositionContext);
  const errorDispatch = useContext(DispatchErrorContext);
  let { id } = useParams();
  let history = useHistory();
  const [positionId, setPositionId] = useState(null);
  const [inviteCandidateModal, setInviteCandidateModal] = useState(false);
  const [moveSelectedApplicationsModal, setMoveSelectedApplicationsModal] = useState(false);
  const [promoteHiBoost, setPromoteHiBoost] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { dispatch } = useContext(PageContext);
  const { compactApplications, loadingCompactApplications, selectedIds } = useKanbanState();
  const { dispatch: pageDispatch } = useContext(PageContext);
  const hasSelections = selectedIds.size > 0;

  const compactApplicationsCount = Object.values(compactApplications || {}).reduce((total, applications) => total + applications.length, 0);

  // This function will be called when the view is going from "edit position view" to "position view"
  const callbackGetPositionPage = useCallback(() => {
    getPositionPageToState();
  }, []);

  let isMounted = true;
  const getPositionPageToState = async () => {
    // Start loading
    setLoadingPage(true);
    try {
      pageDispatch({ type: "init_page" });
      const response = await getPositionPage({ selector: id });
      if (isMounted) {
        if (response.success) {
          dispatch({ type: "page_loaded", payload: response.data });
          setLoadingPage(false);
        }
      }
    } catch (err) {
      // Check if the component is still mounted
      if (isMounted) {
        pageDispatch({ type: "error" });
        errorDispatch({ type: "set_error", payload: err.message });
      }
    }
    // Cleanup function to set isMounted to false when the component is unmounted
    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    getPositionPageToState();
  }, [id, pageDispatch]);

  useEffect(() => {
    positionDispatch({ type: "edit_position_view", payload: false });
  }, []);

  //set this as key back to
  useEffect(() => {
    //save to key back location
    let currentAdminSettings = {};
    if (getGenericLocalStorage("admin_settings")) {
      currentAdminSettings = getGenericLocalStorage("admin_settings", true);
    }
    setGenericLocalStorage(
      "admin_settings",
      {
        ...currentAdminSettings,
        keyBackLocation: `/position/${positionId}`,
      },
      true,
    );
  }, [positionId]);

  useEffect(() => {
    setPositionId(id);
  }, [id]);

  useEffect(() => {
    const fetchPosition = async () => {
      positionDispatch({ type: "init_single_position" });
      let request = {
        selector: id,
        params: { includeSignedRequest: 1 },
      };
      if (id === "unsolicited") {
        request = {
          params: { unsolicited: 1, includeSignedRequest: 1 },
        };
      }
      try {
        const response = await getPositions(request);
        if (response.success && response.data !== null) {
          positionDispatch({
            type: "loaded_single_position",
            payload: { data: response.data, t: t },
          });
          setPositionId(response.data.id);
        } else {
          errorDispatch({
            type: "set_error",
            payload: t("error.could_not_get_position"),
          });
          history.push("/404");
        }
      } catch (err) {
        console.log(err);
      }
    };

    if (id) {
      fetchPosition();
    }
  }, [id, positionId, positionDispatch, errorDispatch, history]);

  const handleInviteCandidate = () => {
    setInviteCandidateModal((prevState) => !prevState);
  };

  const handleMoveSelectedApplications = () => {
    setMoveSelectedApplicationsModal((prevState) => !prevState);
  };

  const openConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleHiBoost = () => {
    setPromoteHiBoost((prevState) => !prevState);
  };

  const handlePromoteConfirmation = (value) => {
    setPromoteHiBoost(value);
  };

  return (
    <>
      <AllPositions isHidden />
      <Application isVisible={false} />
      <SinglePositionContainer viewCount={count} collapse={collapsed}>
        {!editPosition && (
          <StyledTopBar>
            <TopBarTop>
              {single && !isLoading ? <MegaHeader text={single.title === "Unsolicited applications" ? t("common.application.unsolicited_applications") : single.title} /> : <MegaHeader />}
            </TopBarTop>
            <TopBarBottom>
              <TopBarBottomContent>
                <MobileOnly>
                  <div>
                    <EditButton
                      onClick={
                        !editPosition
                          ? () => {
                              positionDispatch({
                                type: "edit_position_view",
                                payload: true,
                              });
                            }
                          : null
                      }
                    >
                      {t("common.editPosition")}
                      <Icon icon="edit" mr="5" />
                    </EditButton>
                  </div>
                </MobileOnly>
                <DesktopOnly>
                  <HiddenBetween maxWidth="1350">
                    <Row>
                      <Col vCenter>
                        <Typography tag="p" nomargin color="#181F38" large lowOpacity>
                          {t("common.createdOn")} {formatDate(single?.page?.createdAt)}
                        </Typography>
                      </Col>
                      <Col vCenter>
                        <Typography tag="p" nomargin mr={5} color="#181F38" large lowOpacity>
                          {t("common.applicationsInTotal")}
                          {":"}
                        </Typography>
                        {loadingCompactApplications ? (
                          <BoxLoader tiny />
                        ) : (
                          <Typography tag="p" nomargin color="#181F38" large lowOpacity>
                            {" "}
                            {compactApplicationsCount}
                          </Typography>
                        )}
                      </Col>
                    </Row>
                  </HiddenBetween>
                </DesktopOnly>
                <Flex widthSM="fit-content;">
                  <Row>
                    <Col>
                      <AnonymousHiring />
                    </Col>
                    <Col>
                      <PositionPublish loadingPage={loadingPage} />
                    </Col>
                    <Col>
                      <TogglePosition />
                    </Col>
                  </Row>
                </Flex>
              </TopBarBottomContent>
            </TopBarBottom>
          </StyledTopBar>
        )}
        {/* SINGLE POSITION */}
        <ApplicationContextWrapper positionId={single.id}>
          <Grid expand fluid column>
            {!editPosition ? (
              <>
                <Row backgroundColor="#F3F3F5" height="100%">
                  <DesktopOnly fullWidth>
                    <Row width="100%;" noMargin paddingSM="15px 15px 15px 15px">
                      <Col vCenter gap="15" $wrap>
                        <PrimaryButton
                          white
                          onClick={
                            !editPosition
                              ? () => {
                                  positionDispatch({
                                    type: "edit_position_view",
                                    payload: true,
                                  });
                                }
                              : null
                          }
                        >
                          {t("common.editPosition")}
                          <Icon icon="edit" mr="10" />
                        </PrimaryButton>
                        {moveSelectedApplicationsModal ? (
                          <Modal title="Move selected applications" onClose={handleMoveSelectedApplications}>
                            <MoveSelectedApplications onClose={handleMoveSelectedApplications} />
                          </Modal>
                        ) : null}
                        {hasSelections && (
                          <PrimaryButton white onClick={handleMoveSelectedApplications}>
                            {t("common.moveSelectedTo", "Move selected to") + "..."}
                            <Icon icon="arrow_u_turn_right_down" mr="10" />
                          </PrimaryButton>
                        )}

                        {/* </Col>
                      <Col right vCenter> */}
                        <ManageShareholders title={single.title} positionId={single.id} />
                        {showConfirmationModal && (
                          <Modal title={t("common.inviteCandidateConfirmationTitle")} onClose={() => setShowConfirmationModal(false)}>
                            <Grid padding="32" alignCenter>
                              <Typography tag="p" nomargin>
                                {t("common.inviteCandidateConfirmationDesc")}
                              </Typography>
                              <PrimaryButton transparent onClick={() => setShowConfirmationModal(false)}>
                                {t("common.great")}
                                <Icon icon="thumbs_up" mr="8" lowOpacity invert />
                              </PrimaryButton>
                            </Grid>
                          </Modal>
                        )}
                        {inviteCandidateModal ? (
                          <Modal title={t("page.invite_candidate.invite_candidate", "Invite candidate")} onClose={handleInviteCandidate}>
                            <InviteCandidate onClose={handleInviteCandidate} openConfirmationModal={openConfirmationModal} />
                          </Modal>
                        ) : null}
                        <PrimaryButton white onClick={handleInviteCandidate}>
                          {t("common.inviteCandidate")}
                          <Icon icon="add_person" mr="10" />
                        </PrimaryButton>
                        {promoteHiBoost ? (
                          <Modal title={t("common.promotewithHiBoost", "Promote with HiBoost")} onClose={handleHiBoost}>
                            <PromoteHiBoost single={single} onClose={handleHiBoost} onPromoteConfirmation={handlePromoteConfirmation} />
                          </Modal>
                        ) : null}
                        <PrimaryButton red onClick={handleHiBoost}>
                          {t("common.promotewithHiBoost")}
                          <Icon icon="rocket" mr="10" />
                        </PrimaryButton>
                      </Col>
                    </Row>
                  </DesktopOnly>
                  <Col size={24}>
                    <Kanban title={single.title} />
                  </Col>
                </Row>
              </>
            ) : (
              <Grid scrollable editPosition>
                <EditPositionView callbackGetPositionPage={callbackGetPositionPage} />
              </Grid>
            )}
          </Grid>
        </ApplicationContextWrapper>
      </SinglePositionContainer>
    </>
  );
};

export default Position;
